import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import formatValue from "../../utils/formatValue";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { useAuth } from "../../hooks/auth";
import Layout from "../../components/Layout";
import Loading from "../../components/Loading";
import {
    Container,
    ErrorMessageContainer,
    Content,
    PropertiesContainer,
    InitialContainer,
    PropertiesTable,
    PagesContainer,
    PageOptionPrevious,
    PageOption,
    PageOptionLatter,
    TableButtonsContainer,
} from "./styles";
import {
    FiChevronLeft,
    FiChevronRight,
    FiChevronsLeft,
    FiChevronsRight,
    FiEye,
} from "react-icons/fi";
import { useToast } from "../../hooks/toast";
import { useRefresh } from "../../hooks/refresh";
import SearchBarPropertiesPage from "../../components/SearchBarPropertiesPage";

interface IProperty {
    id: number;
    title: string;
    is_rented: number;
    rent_value: string;
    address: string;
    neighborhood: string;
    number: string;
    floor?: string | null;
}

interface IParams {
    page: string;
}

function buildUrl(page: number, searchTerm: string) {
    const qs = new URLSearchParams();
    if (searchTerm) qs.set("search", searchTerm);
    const q = qs.toString();
    return q ? `/my-properties/${page}?${q}` : `/my-properties/${page}`;
}

const MyProperties: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { params } = useRouteMatch<IParams>();

    const { dateToExpires, access_token, user } = useAuth();
    const { addToast } = useToast();
    const { refresh } = useRefresh();

    const urlSearch = useMemo(
        () => new URLSearchParams(location.search).get("search") ?? "",
        [location.search]
    );

    const [properties, setProperties] = useState<IProperty[]>([]);
    const [lastPage, setLastPage] = useState<number>(0);
    const [loadingApiProperties, setLoadingApiProperties] = useState(true);
    const [loadApiError, setLoadApiError] = useState("");
    const [listRefreshing, setListRefreshing] = useState(false);
    const hasLoadedListOnceRef = useRef(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [params.page]);

    const applySearch = useCallback(
        (q: string) => {
            const target = buildUrl(1, q);
            const loc = history.location;
            const current = `${loc.pathname}${loc.search || ""}`;
            if (target !== current) {
                history.replace(target);
            }
        },
        [history]
    );

    useEffect(() => {
        const term = urlSearch;

        async function loadApi() {
            if (!hasLoadedListOnceRef.current) {
                setLoadingApiProperties(true);
            } else {
                setListRefreshing(true);
            }

            try {
                const token = await refreshToken(dateToExpires, access_token);

                api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

                const response = await api.get("/api/adm/properties", {
                    params: {
                        page: Number(params.page),
                        search: term || undefined,
                    },
                });

                setProperties(response.data.data.data ?? []);
                setLastPage(response.data.data.last_page ?? 0);
                setLoadApiError("");
            } catch (err: any) {
                if (err?.response?.status === 403) {
                    setLoadApiError(err?.response?.data?.message ?? "Acesso negado");
                }

                if (err?.response?.data?.message) {
                    addToast({
                        title: err.response.data.message,
                        type: "error",
                    });
                }

                setLoadApiError(err?.response?.data?.message ?? "Erro ao carregar imóveis");
            } finally {
                setLoadingApiProperties(false);
                setListRefreshing(false);
                hasLoadedListOnceRef.current = true;
            }
        }

        if (user) {
            loadApi();
        } else {
            history.push("/");
        }
    }, [
        history,
        access_token,
        dateToExpires,
        user,
        params.page,
        urlSearch,
        refresh,
        addToast,
    ]);

    const pagesNumber = useMemo(() => {
        const pagesNumberArray = [];

        for (let i = 1; i <= lastPage; i++) {
            pagesNumberArray.push(i);
        }

        if (pagesNumberArray.length > 5 && Number(params.page) < 5) {
            pagesNumberArray.length = 5;
            return [...pagesNumberArray];
        }

        if (
            pagesNumberArray.length > 5 &&
            Number(params.page) >= 5 &&
            Number(params.page) < pagesNumberArray.length - 3
        ) {
            return [
                pagesNumberArray[Number(params.page) - 4],
                pagesNumberArray[Number(params.page) - 3],
                pagesNumberArray[Number(params.page) - 2],
                pagesNumberArray[Number(params.page) - 1],
                pagesNumberArray[Number(params.page)],
                pagesNumberArray[Number(params.page) + 1],
                pagesNumberArray[Number(params.page) + 2],
            ];
        }

        if (
            pagesNumberArray.length > 5 &&
            Number(params.page) >= pagesNumberArray.length - 4
        ) {
            return [
                pagesNumberArray[pagesNumberArray.length - 5],
                pagesNumberArray[pagesNumberArray.length - 4],
                pagesNumberArray[pagesNumberArray.length - 3],
                pagesNumberArray[pagesNumberArray.length - 2],
                pagesNumberArray[pagesNumberArray.length - 1],
            ];
        }

        return pagesNumberArray;
    }, [lastPage, params.page]);

    return (
        <Container>
            <Layout>
                {loadingApiProperties && !hasLoadedListOnceRef.current && <Loading />}

                {!loadingApiProperties && (
                    <>
                        {!!loadApiError && (
                            <ErrorMessageContainer>
                                <h2>{loadApiError}</h2>
                            </ErrorMessageContainer>
                        )}

                        {!loadApiError && (
                            <Content>
                                <PropertiesContainer>
                                    <InitialContainer>
                                        <h3>Meus imóveis</h3>
                                    </InitialContainer>

                                    <SearchBarPropertiesPage
                                        term={urlSearch}
                                        onApplySearch={applySearch}
                                    />

                                    <div className="relative">
                                    {listRefreshing ? (
                                        <div
                                            className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center bg-white/50"
                                            aria-busy="true"
                                            aria-live="polite"
                                        >
                                            <span
                                                className="inline-block h-9 w-9 animate-spin rounded-full border-2 border-orange-400 border-t-transparent"
                                                role="status"
                                                aria-label="Atualizando lista"
                                            />
                                        </div>
                                    ) : null}
                                    <PropertiesTable>
                                        <thead>
                                            <tr>
                                                <th className="th-code">Código</th>
                                                <th>Título</th>
                                                <th>Endereço</th>
                                                <th>Situação</th>
                                                <th>Valor</th>
                                                <th>Ações</th>
                                            </tr>
                                        </thead>
                                            <tbody>
                                            {properties.length > 0 ? (
                                                properties.map((property) => (
                                                <tr key={property.id}>
                                                    <td className="th-code">{property.id}</td>
                                                    <td>{property.title}</td>
                                                    <td>{`${property.address}, ${property.neighborhood}, ${property.number}${
                                                        property.floor != null &&
                                                        String(property.floor).trim() !== ""
                                                            ? `, ${String(property.floor).trim()}`
                                                            : ""
                                                    }`}</td>
                                                    <td>{property.is_rented ? "Alugado" : "Publicado"}</td>
                                                    <td>{formatValue(parseFloat(property.rent_value))}</td>
                                                    <td>
                                                    <TableButtonsContainer>
                                                        <button
                                                        title="Ver detalhes"
                                                        type="button"
                                                        onClick={() => history.push(`/my-properties/view/${property.id}`)}
                                                        >
                                                        <FiEye color="#04c5ff" size={22} />
                                                        </button>
                                                    </TableButtonsContainer>
                                                    </td>
                                                </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                <td colSpan={6} style={{ textAlign: "center", padding: "24px" }}>
                                                    Nenhum imóvel encontrado.
                                                </td>
                                                </tr>
                                            )}
                                            </tbody>
                                    </PropertiesTable>
                                    </div>

                                    <PagesContainer>
                                        <PageOptionPrevious>
                                            {Number(params.page) !== 1 ? (
                                                <Link to={buildUrl(1, urlSearch)}>
                                                    <FiChevronsLeft color="#666666" size={24} />
                                                </Link>
                                            ) : (
                                                <FiChevronsLeft color="#666666" size={24} />
                                            )}
                                        </PageOptionPrevious>

                                        <PageOptionPrevious>
                                            {Number(params.page) !== 1 ? (
                                                <Link to={buildUrl(Number(params.page) - 1, urlSearch)}>
                                                    <FiChevronLeft color="#666666" size={24} />
                                                </Link>
                                            ) : (
                                                <FiChevronLeft color="#666666" size={24} />
                                            )}
                                        </PageOptionPrevious>

                                        {pagesNumber.map((page) => (
                                            <PageOption
                                                key={page}
                                                isSelected={Number(params.page) === page}
                                            >
                                                <Link to={buildUrl(page, urlSearch)}>{page}</Link>
                                            </PageOption>
                                        ))}

                                        <PageOptionLatter>
                                            {Number(params.page) !== pagesNumber[pagesNumber.length - 1] ? (
                                                <Link to={buildUrl(Number(params.page) + 1, urlSearch)}>
                                                    <FiChevronRight color="#666666" size={24} />
                                                </Link>
                                            ) : (
                                                <FiChevronRight color="#666666" size={24} />
                                            )}
                                        </PageOptionLatter>

                                        <PageOptionLatter>
                                            {Number(params.page) !== pagesNumber[pagesNumber.length - 1] ? (
                                                <Link to={buildUrl(Number(lastPage), urlSearch)}>
                                                    <FiChevronsRight color="#666666" size={24} />
                                                </Link>
                                            ) : (
                                                <FiChevronsRight color="#666666" size={24} />
                                            )}
                                        </PageOptionLatter>
                                    </PagesContainer>
                                </PropertiesContainer>
                            </Content>
                        )}
                    </>
                )}
            </Layout>
        </Container>
    );
};

export default MyProperties;